import {
  Body,
  Callout,
  Heading,
  IconButton,
  Toggle,
} from 'copilot-design-system';
import { useFormikContext } from 'formik';
import React from 'react';

import { ProductStoreVisibility } from 'src/routes/billing/store/schema';

type Props = {
  visibility: ProductStoreVisibility;
  onCopyDomainUrl: () => void;
  basePortalDomain: string;
};

export const ShareProductStoreForm: React.FC<Props> = ({
  visibility,
  onCopyDomainUrl,
  basePortalDomain,
}) => {
  const { handleBlur, setFieldValue } = useFormikContext();

  const isWebsiteChecked =
    visibility === ProductStoreVisibility.Public ||
    visibility === ProductStoreVisibility.All;

  const isPortalChecked =
    visibility === ProductStoreVisibility.ClientUser ||
    visibility === ProductStoreVisibility.All;

  const getStoreVisibility = (
    publicChecked: boolean,
    privateChecked: boolean,
  ) => {
    if (publicChecked && privateChecked) return ProductStoreVisibility.All;
    if (publicChecked) return ProductStoreVisibility.Public;
    if (privateChecked) return ProductStoreVisibility.ClientUser;
    return '';
  };

  const handleToggle = (type: ProductStoreVisibility) => {
    const isWebsiteVisibilityChecked =
      type === ProductStoreVisibility.Public
        ? !isWebsiteChecked
        : isWebsiteChecked;

    const isPortalVisibilityChecked =
      type === ProductStoreVisibility.ClientUser
        ? !isPortalChecked
        : isPortalChecked;

    const newVisibility = getStoreVisibility(
      isWebsiteVisibilityChecked,
      isPortalVisibilityChecked,
    );

    setFieldValue(
      'productStorePreferences.visibility',
      newVisibility || ProductStoreVisibility.Internal,
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <Body>
        By default, only your team can view this store. Select who else should
        be able to access it.
      </Body>
      <div className="flex flex-col gap-3">
        <div className="flex">
          <Toggle
            label=""
            description=""
            checked={isPortalChecked}
            onChange={() => handleToggle(ProductStoreVisibility.ClientUser)}
            onBlur={handleBlur}
          />
          <div>
            <Heading className="leading-4">Portal</Heading>
            <Body size="xs" className="text-gray-small mt-1 leading-5">
              Visible to logged-in clients in your portal.
            </Body>
          </div>
        </div>
        <div className="flex">
          <Toggle
            label=""
            description=""
            checked={isWebsiteChecked}
            onChange={() => handleToggle(ProductStoreVisibility.Public)}
          />

          <div className="w-full">
            <Heading className="leading-4">Public website</Heading>
            <Body size="xs" className="text-gray-small mb-0.5 mt-1 leading-5">
              Visible with public link.
            </Body>
            {(visibility === ProductStoreVisibility.Public ||
              visibility === ProductStoreVisibility.All) && (
              <Callout
                variant="neutral"
                description={
                  <div className="flex justify-between items-center gap-2.5">
                    <Body className="break-all">{basePortalDomain}</Body>
                    <IconButton
                      icon="Copy"
                      className="text-xs p-0 w-4.5 h-4.5 text-gray-small"
                      onClick={onCopyDomainUrl}
                      variant="minimal"
                    />
                  </div>
                }
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
